/**
 * @file Support info modal.
 */

import React, { Fragment, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import compose from 'lodash.flowright';
import { Formik } from 'formik';
import { graphql } from 'react-apollo';

import withModal from '../../global/modal/withModal';
import { SUPPORT_MODAL } from '../../../constants/modals';
import Button from '../../global/Button';
import { getAsset } from '../../../util/assets';
import Textarea from '../../global/Textarea';
import Alert from '../../global/Alert';
import { FeedbackCreate } from './graphql/mutations';
import { getErrorString } from '../../../util/normalize-errors';
import { openAlert as openAlertAction } from '../../../actions/modal-actions';
import FormGroup from '../../global/FormGroup';
import { MeQuery } from '../../../graphql/queries/account';

const propTypes = {
  feedbackCreate: PropTypes.func.isRequired,
  openAlert: PropTypes.func,
  user: PropTypes.shape({
    email_address: PropTypes.string,
  }),
  actionProps: PropTypes.shape({
    defaultMessage: PropTypes.string,
  }),
};

const SupportModal = ({
  feedbackCreate,
  openAlert,
  user,
  actionProps: { defaultMessage = '' } = {},
}) => {
  const textarea = useRef();
  const location = useLocation();
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const onValidate = values => {
    const errors = {};

    if (!values.message) {
      errors.message = 'A message must be included to submit this form';
    }

    if (!values.email) {
      errors.email = 'An email address must be provided to submit this form';
    }

    return errors;
  };

  const onSubmit = async ({ email, message }) => {
    setSubmitting(true);

    try {
      await feedbackCreate({
        email,
        message,
        url: location.pathname,
      });
      setSuccess(true);
    } catch (err) {
      openAlert({
        message:
          'There was an error attempting to submit this message. Please try again later.',
        info: getErrorString(err),
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="support-modal-content">
        <img
          src={getAsset('/waldo-excited.png')}
          width="60px"
          alt="Waldo icon"
          className="mb-2"
        />
        <h3>Thanks for your message!</h3>
        <p className="mb-3">Our support team will be reviewing it shortly.</p>
        <Button onClick={() => setSuccess(false)} className="support-modal-btn">
          Send another message
        </Button>
      </div>
    );
  }

  return (
    <div className="support-modal-content">
      <img
        src={getAsset('/waldo-icon.svg')}
        width="60px"
        alt="Waldo icon"
        className="mb-2"
      />
      <h3>Have feedback, suggestions, or need help?</h3>
      <p className="mb-3">We&apos;d love to hear from you!</p>

      <Formik
        onSubmit={onSubmit}
        validate={onValidate}
        initialValues={{
          message: defaultMessage ?? '',
          email: user?.email_address ?? '',
        }}
        render={({ values, errors, handleSubmit, handleChange }) => {
          const errorList = Object.values(errors);

          return (
            <Fragment>
              <form onSubmit={handleSubmit} className="px-sm-2">
                <FormGroup
                  id="email"
                  label="Your Email Address"
                  type="text"
                  placeholder="example@email.com"
                  labelType="expanded"
                  onChange={handleChange}
                  value={values.email}
                  className="mb-2"
                  required
                />
                <Textarea
                  id="message"
                  placeholder="Drop your feedback, suggestions, or help requests here!"
                  inputRef={textarea}
                  onChange={handleChange}
                  value={values.message}
                  className="mb-2 support-modal-textarea"
                  rows={10}
                  cols={30}
                />
                {errorList.length > 0 && (
                  <Alert type="danger" className="mt-1">
                    <p className="text-bold">{errorList[0]}</p>
                  </Alert>
                )}
                <Button
                  disabled={submitting}
                  type="submit"
                  className="support-modal-btn"
                >
                  Submit
                </Button>
              </form>
            </Fragment>
          );
        }}
      />
    </div>
  );
};

SupportModal.propTypes = propTypes;

const withFeedbackCreate = graphql(FeedbackCreate, {
  props: ({ mutate }) => ({
    feedbackCreate: ({ email, message, url }) =>
      mutate({
        variables: {
          input: {
            platform: 'WEB',
            email_address: email,
            message,
            url,
          },
        },
      }),
  }),
});

const withUser = graphql(MeQuery, {
  props: ({ data: { me } }) => ({
    user: me,
  }),
  skip: ({ authenticated }) => !authenticated,
});

const mapStateToProps = ({ auth }) => ({
  authenticated: auth.authenticated,
});

const mapDispatchToProps = {
  openAlert: openAlertAction,
};

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withFeedbackCreate,
  withUser,
  withModal,
)(SupportModal, {
  name: SUPPORT_MODAL,
  size: 'sm',
});
